import type { MRT_Row } from "mantine-react-table-open";
import { useCallback, useState } from "react";
import type { BasePeriodRow, PeriodBasedRow } from "../PeriodBasedTable.types";

type PendingDateChanges = {
  [rowId: string]: Partial<BasePeriodRow>;
};

type DateField = keyof BasePeriodRow;

export function getRowId<
  T extends Record<string, unknown> = Record<string, unknown>
>(row: BasePeriodRow & T): string {
  const { first_date, last_date, ...otherFields } = row;
  const firstValue =
    Object.values(otherFields as Record<string, unknown>)[0] || "";
  return `${first_date}${firstValue}${last_date}`;
}

interface UsePeriodBasedTableProps<T extends Record<string, unknown>> {
  tableRows: Array<BasePeriodRow & T>;
  setTableRows: React.Dispatch<React.SetStateAction<Array<BasePeriodRow & T>>>;
  notifyChange: (rows: Array<BasePeriodRow & T>) => void;
}

export function usePeriodBasedTable<T extends Record<string, unknown>>({
  tableRows,
  setTableRows,
  notifyChange
}: UsePeriodBasedTableProps<T>) {
  const [pendingDateChanges, setPendingDateChanges] =
    useState<PendingDateChanges>({});
  const [rowToDelete, setRowToDelete] = useState<MRT_Row<
    PeriodBasedRow<T>
  > | null>(null);
  const [rowToEdit, setRowToEdit] = useState<{
    row: MRT_Row<PeriodBasedRow<T>>;
    values: Record<string, unknown>;
    exitEditingMode: () => void;
  } | null>(null);

  const updateRowDateField = useCallback(
    (
      rowId: string,
      dateField: DateField,
      newDate: string,
      isTemporary = false
    ) => {
      if (isTemporary) {
        setPendingDateChanges((prev) => ({
          ...prev,
          [rowId]: { ...prev[rowId], [dateField]: newDate }
        }));
        return;
      }

      setTableRows((prevRows) => {
        const updatedRows = prevRows.map((row) =>
          getRowId(row) === rowId
            ? ({ ...row, [dateField]: newDate } as BasePeriodRow & T)
            : row
        );
        notifyChange(updatedRows);
        return updatedRows;
      });
    },
    [notifyChange, setTableRows]
  );

  const handleUpdatePreviousRowLastDate = useCallback(
    (rowId: string, newDate: string, isTemporary = false) =>
      updateRowDateField(rowId, "last_date", newDate, isTemporary),
    [updateRowDateField]
  );

  const handleUpdateNextRowFirstDate = useCallback(
    (rowId: string, newDate: string, isTemporary = false) =>
      updateRowDateField(rowId, "first_date", newDate, isTemporary),
    [updateRowDateField]
  );

  const handleDeleteRow = useCallback(
    (row: MRT_Row<PeriodBasedRow<T>>) => setRowToDelete(row),
    []
  );

  const updateRowAtIndex = useCallback(
    (
      rows: Array<BasePeriodRow & T>,
      index: number,
      field: DateField,
      value: string
    ) =>
      rows.map((row, i) =>
        i === index ? ({ ...row, [field]: value } as BasePeriodRow & T) : row
      ),
    []
  );

  const handleConfirmDelete = useCallback(() => {
    if (!rowToDelete) return;

    const targetRowId = rowToDelete.id;
    const targetRowIndex = tableRows.findIndex(
      (row) => getRowId(row) === targetRowId
    );

    if (targetRowIndex === -1) return;

    const rowsWithoutTarget = tableRows.filter(
      (row) => getRowId(row) !== targetRowId
    );
    const isFirstRow = targetRowIndex === 0;
    const isLastRow = targetRowIndex === tableRows.length - 1;
    const hasOtherRows = tableRows.length > 1;

    let finalRows = rowsWithoutTarget;

    if (!isFirstRow && !isLastRow) {
      const nextRowIndex = targetRowIndex;
      const deletedStartDate = rowToDelete.getValue("first_date") as string;
      finalRows = updateRowAtIndex(
        finalRows,
        nextRowIndex,
        "first_date",
        deletedStartDate
      );
    } else if (isLastRow && hasOtherRows) {
      const previousRowIndex = targetRowIndex - 1;
      const deletedEndDate = rowToDelete.getValue("last_date") as string;
      finalRows = updateRowAtIndex(
        finalRows,
        previousRowIndex,
        "last_date",
        deletedEndDate
      );
    }

    setTableRows(finalRows);
    notifyChange(finalRows);
    setRowToDelete(null);
    setPendingDateChanges({});
  }, [tableRows, rowToDelete, notifyChange, setTableRows, updateRowAtIndex]);

  const handleConfirmEdit = useCallback(() => {
    if (!rowToEdit) return;

    const { row, values } = rowToEdit;
    const editedRowIndex = row.index;

    const originalRowInTableRows = tableRows[editedRowIndex];
    const editedRowId = originalRowInTableRows
      ? getRowId(originalRowInTableRows)
      : null;

    const updatedRows = tableRows.map((tableRow, index) => {
      const currentRowId = getRowId(tableRow);

      if (index === editedRowIndex || currentRowId === editedRowId) {
        return { ...tableRow, ...values } as PeriodBasedRow<T>;
      } else {
        const pendingChanges = pendingDateChanges[currentRowId];
        return pendingChanges ? { ...tableRow, ...pendingChanges } : tableRow;
      }
    });

    setTableRows(updatedRows);
    notifyChange(updatedRows);
    setPendingDateChanges({});
    setRowToEdit(null);
    rowToEdit.exitEditingMode();
  }, [rowToEdit, tableRows, pendingDateChanges, notifyChange, setTableRows]);

  return {
    pendingDateChanges,
    setPendingDateChanges,
    rowToDelete,
    setRowToDelete,
    rowToEdit,
    setRowToEdit,
    handleUpdatePreviousRowLastDate,
    handleUpdateNextRowFirstDate,
    handleDeleteRow,
    handleConfirmDelete,
    handleConfirmEdit
  };
}
